/**
 * Shared enumerations and constants for ClearIntake.
 * Import from @clearintake/shared.
 */

export enum TokenType {
  MessageAccess = 'message_access',
  FormAssignment = 'form_assignment',
  OTP = 'otp',
  PackageRun = 'package_run',
}

export enum ActorType {
  Staff = 'staff',
  Contact = 'contact',
  System = 'system',
}

export enum FormAIMode {
  AIOnly   = 'ai_only',
  Toggle   = 'toggle',
  FormOnly = 'form_only',
}

/**
 * Readability alias for FormAIMode.Toggle. Points at the canonical enum member
 * rather than re-declaring the union value, so the enum has no duplicate-valued
 * members (avoids masking typos / breaking exhaustiveness checks).
 */
export const ToggleMode = FormAIMode.Toggle;

export enum FormAuthMode {
  Open = 'open',
  OTPEmail = 'otp_email',
  OTPPhone = 'otp_phone',
}

export enum FromType {
  Staff = 'staff',
  Contact = 'contact',
}

export enum AuditAction {
  // Messages
  MessageRead          = 'message.read',
  MessageSent          = 'message.sent',
  MessageReplied       = 'message.replied',
  AttachmentUploaded   = 'attachment.uploaded',
  AttachmentDownloaded = 'attachment.downloaded',
  // Forms
  FormSubmitted        = 'form.submitted',
  FormAssigned         = 'form.assigned',
  FormViewed           = 'form.viewed',
  FormSchemaCreated    = 'form.schema.created',
  FormSchemaUpdated    = 'form.schema.updated',
  FormSchemaDeleted    = 'form.schema.deleted',
  SubmissionViewed     = 'submission.viewed',
  PDFAccessed          = 'pdf.accessed',
  // Form packages
  PackageCreated       = 'package.created',
  PackageUpdated       = 'package.updated',
  PackageDeleted       = 'package.deleted',
  PackageAssigned      = 'package.assigned',
  PackageRunViewed     = 'package.run.viewed',
  PackageRunCompleted  = 'package.run.completed',
  // AI
  AIIntakeStarted      = 'ai.intake.started',
  AIIntakeCompleted    = 'ai.intake.completed',
  // Auth
  AuthOTPRequested     = 'auth.otp.requested',
  AuthOTPVerified      = 'auth.otp.verified',
  AuthSSOVerified      = 'auth.sso.verified',
  // Tenant / GHL
  SettingsUpdated      = 'settings.updated',
  GHLDisconnected      = 'ghl.disconnected',
  ContactAccessed      = 'contact.accessed',
  WebhookReceived      = 'webhook.received',
  AppInstalled         = 'app.installed',
  AppUninstalled       = 'app.uninstalled',
  TenantDisabledBlocked = 'tenant.disabled_blocked',
  // Other
  PDFGenerated         = 'pdf.generated',
  ContactMatched       = 'contact.matched',
}

/** Magic link token lifetime in seconds */
export const MAGIC_LINK_TTL_SECONDS = 60 * 60 * 24 * 7; // 7 days

/** OTP lifetime in seconds */
export const OTP_TTL_SECONDS = 60 * 10; // 10 minutes

/** Staff JWT lifetime in seconds */
export const STAFF_JWT_TTL_SECONDS = 60 * 60 * 8; // 8 hours

/** Contact JWT lifetime in seconds */
export const CONTACT_JWT_TTL_SECONDS = 60 * 60 * 2; // 2 hours

/** Run grant (public package run) lifetime in seconds */
export const RUN_GRANT_TTL_SECONDS = 7200; // 2 hours

/** OTP code length */
export const OTP_LENGTH = 6;

/** GHL API base URL */
export const GHL_API_BASE = 'https://services.leadconnectorhq.com';

// ---------------------------------------------------------------------------
// Submitter Identity + Form Tokens
// ---------------------------------------------------------------------------

/**
 * Default identity config used when a form/package has no `identity_fields_json`
 * (NULL). firstName + lastName + dob, all required. `import type` avoids a value
 * import from ./types (which imports from here) — enums are the only value cycle.
 */
export const DEFAULT_IDENTITY_FIELDS: import('../types/index.js').IdentityFieldConfig[] = [
  { key: 'firstName', required: true },
  { key: 'lastName', required: true },
  { key: 'dob', required: true },
];

/**
 * Maps a legacy schema `{{token}}` placeholder to the identity field that fills
 * it. {{birthDate}} and {{dob}} both map to `dob`. Any other `{{...}}`-shaped
 * whole-value defaultValue (e.g. {{orgId}}) is NOT here and is swept to '' at
 * serve time.
 */
export const IDENTITY_TOKEN_MAP: Record<string, import('../types/index.js').IdentityFieldKey> = {
  '{{firstName}}': 'firstName',
  '{{lastName}}': 'lastName',
  '{{birthDate}}': 'dob',
  '{{dob}}': 'dob',
  '{{email}}': 'email',
  '{{phone}}': 'phone',
};